import React from 'react';
import {
  BarChart3,
  FileSpreadsheet,
  LayoutDashboard,
  MessageCircle,
  Settings,
  UploadCloud,
} from 'lucide-react';
import gabmicLogo from '../../assets/gabmic-logo.png';
import { getSettings, getStoredDataset } from '../../services/analytics';

const links = [
  { name: 'Overview', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Upload', path: '/upload', icon: UploadCloud },
  { name: 'Analytics', path: '/analytics', icon: BarChart3 },
  { name: 'Ask GabMic', path: '/ask', icon: MessageCircle },
  { name: 'Settings', path: '/settings', icon: Settings },
];

const Sidebar: React.FC = () => {
  const activePath = window.location.pathname;
  const dataset = getStoredDataset();
  const settings = getSettings();

  return (
    <aside
      className="desktop-only"
      style={{
        width: '250px',
        padding: '1.35rem 0.9rem',
        borderRight: '1px solid var(--border-color)',
        minHeight: '100vh',
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.028), rgba(255,255,255,0.004)), #090909',
      }}
    >
      <a href="/dashboard" style={{ display: 'flex', alignItems: 'center', padding: '0 0.65rem 2.25rem' }}>
        <img src={gabmicLogo} alt="GabMic" style={{ height: '32px', width: 'auto' }} />
      </a>

      <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        {links.map((link) => (
          <NavItem key={link.name} link={link} isActive={activePath === link.path} />
        ))}
      </nav>

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        {dataset ? (
          <div className="premium-card" style={{ padding: '0.9rem', display: 'flex', gap: '0.7rem', alignItems: 'center' }}>
            <FileSpreadsheet size={20} color="var(--text-secondary)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <strong style={{ display: 'block', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dataset.fileName}</strong>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                <span className="status-dot" />
                {dataset.rows.length.toLocaleString()} rows
              </span>
            </div>
          </div>
        ) : (
          <div className="premium-card" style={{ padding: '0.9rem', display: 'flex', gap: '0.7rem', alignItems: 'center' }}>
            <FileSpreadsheet size={20} color="var(--text-secondary)" />
            <div>
              <strong style={{ display: 'block', fontSize: '0.88rem' }}>No file selected</strong>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>Upload data to begin</span>
            </div>
          </div>
        )}

        <div style={{ padding: '0.5rem 0.45rem 0.1rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          {settings.businessName || 'Business account'}
        </div>
      </div>
    </aside>
  );
};

type NavLinkItem = typeof links[number];

const NavItem: React.FC<{ link: NavLinkItem; isActive: boolean }> = ({ link, isActive }) => {
  const Icon = link.icon;

  return (
    <a
      href={link.path}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.8rem',
        padding: '0.82rem 0.9rem',
        borderRadius: '8px',
        color: isActive ? 'var(--text-main)' : 'var(--text-secondary)',
        background: isActive
          ? 'linear-gradient(90deg, rgba(255,122,0,0.27), rgba(255,122,0,0.075))'
          : 'transparent',
        border: `1px solid ${isActive ? 'rgba(255,122,0,0.25)' : 'transparent'}`,
        boxShadow: isActive ? 'inset 4px 0 0 var(--gabmic-orange)' : 'none',
        fontWeight: isActive ? 760 : 560,
        transition: 'background 0.18s ease, color 0.18s ease, transform 0.18s ease',
      }}
    >
      <Icon size={20} color={isActive ? 'var(--gabmic-orange)' : 'currentColor'} strokeWidth={2} />
      {link.name}
    </a>
  );
};

export default Sidebar;
